// Компонент аватара Героя.
// - Грузит картинку из /img/heroes/<id>.webp (full) или /img/heroes/thumb/<id>.webp
// - При отсутствии файла → SVG-плейсхолдер цвета фракции (через onError)
// - lazy-загрузка: браузер не качает картинку пока она не близка к viewport
// - Fade-in анимация при появлении (нет flash-эффекта пустых рамок)
//
// Использование:
//   <HeroAvatar id="deus_tanaki" size={48} faction="deus" />            // thumb
//   <HeroAvatar id="deus_tanaki" size={80} faction="deus" quality="full" /> // full

import { useState } from 'react';

const FACTION_COLOR: Record<string, string> = {
  deus: '#3D7DFF',
  syntetics: '#C93DFF',
  global: '#FF8A1F',
  livlife: '#FF3D3D',
  neutral: '#9CFF3D'
};

type Quality = 'thumb' | 'full';

interface Props {
  id: string;                                   // ID Героя (e.g. 'deus_tanaki')
  size?: number;                                // px, default 56
  faction?: 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';
  shape?: 'square' | 'circle';                  // default 'square'
  quality?: Quality;                            // 'thumb' (96px) или 'full' (256+px)
  className?: string;
  eager?: boolean;                              // true = грузить сразу (без lazy)
}

export function HeroAvatar({
  id,
  size = 56,
  faction = 'deus',
  shape = 'square',
  quality,
  className,
  eager = false
}: Props) {
  // Если quality не указан — выбираем автоматически: маленькие (< 64px) → thumb,
  // большие → full. На практике большинство мест попадают в "thumb" размер.
  const q: Quality = quality ?? (size >= 64 ? 'full' : 'thumb');
  // Цепочка fallback: пробуем .webp (thumb→full), затем .jpg, затем SVG-плейсхолдер.
  // Это позволяет класть Героев в JPG так же как карты — без обязательной
  // конвертации в WebP.
  const candidates = q === 'thumb'
    ? [`/img/heroes/thumb/${id}.webp`, `/img/heroes/${id}.webp`, `/img/heroes/${id}.jpg`]
    : [`/img/heroes/${id}.webp`, `/img/heroes/${id}.jpg`];
  const src = candidates[0];

  // failed = картинка не загрузилась → показываем SVG-плейсхолдер.
  // loaded = картинка догрузилась → fade-in.
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const color = FACTION_COLOR[faction];
  const radius = shape === 'circle' ? '50%' : '4px';

  const baseStyle: React.CSSProperties = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: radius,
    border: `1.5px solid ${color}`,
    background: 'var(--bg-3)',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  };

  if (failed) {
    return (
      <div className={className} style={baseStyle}>
        <svg
          width={Math.round(size * 0.5)}
          height={Math.round(size * 0.5)}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="1.5"
        >
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21a8 8 0 0 1 16 0" />
        </svg>
      </div>
    );
  }

  // Fallback цепочка: переключаемся на следующий кандидат в списке.
  // candidates[0] уже стоит в src начального рендера.
  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const i = parseInt(img.dataset.idx || '0', 10) + 1;
    if (i < candidates.length) {
      img.dataset.idx = String(i);
      img.src = candidates[i];
    } else {
      setFailed(true);
    }
  };

  return (
    <div className={className} style={baseStyle}>
      <img
        src={src}
        alt={id}
        width={size}
        height={size}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        onError={handleError}
        onLoad={() => setLoaded(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.25s ease'
        }}
      />
      {/* Скелетон фон пока картинка не догрузилась — лёгкая пульсация. */}
      {!loaded && !failed && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${color}15, transparent)`,
            animation: 'avatarSkeleton 1.2s ease-in-out infinite'
          }}
        />
      )}
    </div>
  );
}

// CSS keyframe — добавим через style-тег один раз (модули не дают вставлять
// глобальный keyframe прямо в компонент).
if (typeof document !== 'undefined' && !document.getElementById('hero-avatar-keyframes')) {
  const style = document.createElement('style');
  style.id = 'hero-avatar-keyframes';
  style.textContent = `
    @keyframes avatarSkeleton {
      0%, 100% { opacity: 0.3; }
      50%      { opacity: 0.7; }
    }
  `;
  document.head.appendChild(style);
}
